import createClient from 'openapi-fetch';
import type { components, paths } from './shema';
import { napakaApi, napakaPovezave } from './napaka';
import { seja as privzetaSeja, type Seja } from '../seja/seja';
import { preveriZaklep } from '../narocnina/zaklep';

export type Shema = components['schemas'];

/**
 * fetch z dostopnim žetonom. Ob 401 (žeton potekel) enkrat osveži sejo s piškotkom in zahtevo ponovi; če osvežitev
 * ne uspe, je uporabnik odjavljen (aplikacija pokaže prijavo). 403 NAROCNINA_POTEKLA zaklene aplikacijo na zaslon
 * Naročnina ({@link preveriZaklep}); ob 403 NI_PRAVICE seja znova prebere pravice (vlogo je morda spremenil skrbnik).
 */
export function fetchZSejo(seja: Seja, poslji: typeof fetch = (...a) => fetch(...a)) {
  const zZetonom = (zahteva: Request): Promise<Response> => {
    const z = zahteva.clone();
    // strežnik po tej glavi beleži odjemalca v aktivnosti uporabnikov (skrbništvo)
    z.headers.set('X-Hera-Odjemalec', 'splet');
    const zeton = seja.zeton();
    if (zeton) {
      z.headers.set('Authorization', `Bearer ${zeton}`);
    }
    return poslji(z);
  };
  return async (zahteva: Request): Promise<Response> => {
    let odgovor: Response;
    try {
      odgovor = await zZetonom(zahteva);
      if (odgovor.status === 401 && (await seja.osvezi())) {
        odgovor = await zZetonom(zahteva);
      }
    } catch {
      throw napakaPovezave();
    }
    await preveriZaklep(odgovor);
    await preveriPravice(seja, odgovor);
    return odgovor;
  };
}

/** 403 NI_PRAVICE: pravice v seji so morda zastarele (telo ostane neprebrano za klicatelja). */
async function preveriPravice(seja: Seja, odgovor: Response): Promise<void> {
  if (odgovor.status !== 403) {
    return;
  }
  try {
    const telo = (await odgovor.clone().json()) as { koda?: string };
    if (telo.koda === 'NI_PRAVICE') {
      void seja.osveziPravice();
    }
  } catch {
    // ni JSON
  }
}

/** Odjemalec API-ja s tipi iz openapi.json (npm run api); kliče relativno /api na istem naslovu. */
export const api = createClient<paths>({
  baseUrl: typeof window === 'undefined' ? '' : window.location.origin,
  fetch: fetchZSejo(privzetaSeja),
});

type Odgovor<T> = Promise<{ data?: T; error?: unknown; response: Response }>;

/**
 * Podatki iz odgovora openapi-fetch ali NapakaApi s sporočilom strežnika (za useQuery in obrazce). Telo napake je
 * openapi-fetch že prebral (v {@code error}), zato ga ne beremo iz odgovora znova.
 */
export async function podatki<T>(klic: Odgovor<T>): Promise<T> {
  const { data, error, response } = await klic;
  if (!response.ok) {
    throw napakaApi(error, response.status);
  }
  return data as T;
}

/** Kot {@link podatki} za klice brez vsebine odgovora (npr. 204). */
export async function izvedi(klic: Odgovor<unknown>): Promise<void> {
  await podatki(klic);
}
